/**
 * TravelerPermit — Audiostride's Strides shop, styled as an expedition-
 * journal permit card. Left column shows the user's live avatar on a
 * parchment permit; right column is a category rail (Identity/Gear/Flair)
 * + item grid.
 *
 * Backed by real endpoints:
 *   GET  /avatar/catalog  → per-user enriched catalog + progress + config
 *   POST /avatar/wear     → swap the currently-selected item in a category
 *   POST /avatar/purchase → deduct Strides and add to owned_items
 *
 * All milestone/affordability decisions are server-authoritative — this
 * file just renders and dispatches. Selecting an owned item auto-wears it.
 * Selecting a purchasable-but-not-owned item opens a confirmation modal.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Check, ShieldCheck, Search, X, Sparkles, TrendingUp } from 'lucide-react';
import { useAuth } from '../App';
import { AvatarFigure } from '../components/AvatarFigure';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category rail structure — must match the backend catalog groups but the
// order here defines how the rail renders. Includes the new categories
// (facialhair, eyewear, neckwear) added in the V2 catalog pass.
const RAIL_GROUPS = [
  { label: 'Identity', cats: ['skin', 'hairstyle', 'haircolor', 'eyecolor', 'facialhair', 'eyewear'] },
  { label: 'Gear',     cats: ['footwear', 'outfit', 'headwear', 'neckwear', 'backpack'] },
  { label: 'Flair',    cats: ['companion', 'backdrop'] },
];

// Progress-bar denominator — how many categories we've customized away
// from the default. Anything different from DEFAULT_AVATAR_CONFIG counts.
const DEFAULT_CONFIG_IDS = {
  skin: 'porcelain', hairstyle: 'bald', haircolor: 'hc_black', eyecolor: 'ey_brown',
  facialhair: 'none_fh', eyewear: 'none_ew',
  footwear: 'sneakers', outfit: 'tee_shorts', headwear: 'none_hat', neckwear: 'none_nw',
  backpack: 'none_pack', companion: 'none_comp', backdrop: 'parchment',
};

// Odometer digit tile — mechanical monospace balance display in the permit.
const OdometerDigit = ({ ch }) => {
  if (ch === ',') {
    return <span className="text-amber-400/70 font-mono px-0.5">,</span>;
  }
  return (
    <span
      className="inline-flex items-center justify-center bg-[#241E17] text-amber-200 font-mono font-black w-6 h-8 rounded border border-amber-900/60 shadow-inner"
      style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
    >
      {ch}
    </span>
  );
};

const Odometer = ({ value }) => {
  const s = String(Math.max(0, Math.floor(value || 0)));
  // Insert thousands separators for the odometer look
  const chars = [];
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) chars.push(',');
    chars.push(s[i]);
  }
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="permit-strides-odometer">
      {chars.map((c, i) => <OdometerDigit key={`${c}-${i}`} ch={c} />)}
    </span>
  );
};

// The parchment "Traveler Permit" card. Uses a foil border, dashed inner
// frame, corner brackets, and a small "Verified Traveler" stamp rotated
// -12° in the top-right.
const TravelerPermit = ({ userDisplay, avatarConfig, balance, personalized, total, permitNumber }) => {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  // Subtle 3D tilt tracking the cursor (respect reduce-motion).
  const onMove = (e) => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: nx * 6, y: ny * -6 });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  const pct = total > 0 ? Math.round((personalized / total) * 100) : 0;

  return (
    <div
      ref={wrapRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative"
      style={{ perspective: '1200px' }}
    >
      <div
        className="relative rounded-2xl p-1"
        style={{
          background: 'linear-gradient(140deg, #CBA135 0%, #F0DA92 20%, #CBA135 50%, #7d5f1c 100%)',
          transform: `rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
          transition: 'transform 0.2s ease-out',
        }}
      >
        <div className="relative rounded-2xl bg-[#F4EEDF] px-6 py-5 shadow-2xl overflow-hidden">
          {/* Corner brackets */}
          {['tl', 'tr', 'bl', 'br'].map((corner) => (
            <span
              key={corner}
              aria-hidden
              className={`absolute w-4 h-4 border-amber-900/70 ${
                corner === 'tl' ? 'top-2 left-2 border-t-2 border-l-2' :
                corner === 'tr' ? 'top-2 right-2 border-t-2 border-r-2' :
                corner === 'bl' ? 'bottom-2 left-2 border-b-2 border-l-2' :
                'bottom-2 right-2 border-b-2 border-r-2'
              }`}
            />
          ))}
          {/* Dashed inner border */}
          <div className="absolute inset-3 border border-dashed border-amber-900/25 rounded-xl pointer-events-none" />
          {/* Rotated stamp */}
          <div
            aria-hidden
            className="absolute top-3 right-6 border-2 border-[#C1502E] text-[#C1502E] font-black px-2 py-1 text-[0.55rem] tracking-widest text-center leading-tight rounded-sm opacity-80"
            style={{ transform: 'rotate(-12deg)', fontFamily: '"Barlow Condensed", system-ui' }}
            data-testid="permit-stamp"
          >
            VERIFIED<br />TRAVELER
          </div>

          {/* Head */}
          <div className="flex items-baseline justify-between mb-3 relative z-10">
            <span
              className="text-xs uppercase tracking-widest font-black text-amber-900/80"
              style={{ fontFamily: '"Barlow Condensed", system-ui' }}
            >
              Traveler Permit
            </span>
            <span className="font-mono text-xs text-amber-800" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
              NO. {permitNumber}
            </span>
          </div>

          {/* Avatar stage */}
          <div className="flex justify-center mb-3 relative">
            <div className="rounded-xl overflow-hidden" style={{ background: '#DCD3BD' }}>
              <AvatarFigure config={avatarConfig} size={200} floating />
            </div>
          </div>

          {/* Name */}
          <div
            className="text-center text-2xl leading-tight font-black text-[#241E17] tracking-tight"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            data-testid="permit-name"
          >
            {userDisplay}
          </div>
          <div
            className="text-center text-xs text-amber-900/70 tracking-wide mb-4"
            style={{ fontFamily: '"Source Sans 3", system-ui' }}
          >
            Level {Math.max(1, Math.floor((balance || 0) / 250))} traveler
          </div>

          {/* Balance */}
          <div className="flex items-center justify-between mb-3 bg-[#241E17] rounded-lg px-3 py-2">
            <span
              className="text-[0.6rem] uppercase tracking-widest text-amber-300/80 font-black"
              style={{ fontFamily: '"Barlow Condensed", system-ui' }}
            >
              Strides balance
            </span>
            <Odometer value={balance} />
          </div>

          {/* Progress */}
          <div className="mt-2">
            <div className="flex items-center justify-between text-[0.6rem] uppercase tracking-widest text-amber-900/70 font-black mb-1.5" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
              <span>Look personalized</span>
              <span data-testid="permit-progress-text">{personalized}/{total}</span>
            </div>
            <div className="h-2 rounded-full bg-amber-900/15 overflow-hidden ring-1 ring-amber-900/10">
              <div
                className="h-full bg-gradient-to-r from-[#C1502E] via-[#E07A3A] to-[#CBA135] transition-all duration-500 relative overflow-hidden"
                style={{ width: `${pct}%` }}
              >
                <div className="absolute inset-0 opacity-40" style={{ background: 'linear-gradient(90deg, transparent 40%, rgba(255,255,255,.5) 50%, transparent 60%)', animation: 'shimmer 2.4s ease-in-out infinite' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Item card in the right-panel grid.
const ItemCard = ({ item, category, isSelected, baseConfig, onClick }) => {
  const rarityRibbon = (() => {
    if (item.rarity === 'legendary') {
      return (
        <div className="absolute -top-2 -right-2 rotate-12 bg-gradient-to-r from-[#CBA135] to-[#F0DA92] text-amber-950 text-[0.55rem] font-black uppercase tracking-widest px-2 py-0.5 rounded shadow-md z-10" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
          Legendary
        </div>
      );
    }
    if (item.rarity === 'rare') {
      return (
        <div className="absolute -top-2 -right-2 rotate-12 bg-[#3E7484] text-amber-50 text-[0.55rem] font-black uppercase tracking-widest px-2 py-0.5 rounded shadow-md z-10" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
          Rare
        </div>
      );
    }
    return null;
  })();

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      data-testid={`shop-item-${category}-${item.id}`}
      className={`relative text-left rounded-xl border-2 p-2.5 transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CBA135]
        ${isSelected
          ? 'border-[#CBA135] ring-2 ring-[#F0DA92] bg-[#241E17]/80 shadow-lg shadow-[#CBA135]/20'
          : 'border-amber-900/40 hover:border-[#CBA135]/80 bg-[#241E17]/50 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#CBA135]/10'}
        ${item.locked ? 'opacity-70 cursor-not-allowed hover:translate-y-0 hover:shadow-none' : 'cursor-pointer'}
      `}
    >
      {rarityRibbon}
      {isSelected && (
        <span
          className="absolute top-1.5 left-1.5 bg-[#CBA135] rounded-full p-0.5 shadow z-10"
          data-testid={`shop-item-${category}-${item.id}-selected`}
        >
          <Check className="w-3 h-3 text-amber-950" strokeWidth={4} />
        </span>
      )}
      {item.locked && (
        <span className="absolute top-1.5 left-1.5 bg-amber-900/80 text-amber-100 rounded-full p-1 z-10">
          <Lock className="w-3 h-3" />
        </span>
      )}

      <div className="rounded-lg overflow-hidden mb-2" style={{ background: 'rgba(244,238,223,0.06)' }}>
        <div className="flex items-center justify-center h-20">
          {item.color && ['skin', 'haircolor', 'eyecolor', 'backdrop'].includes(category) ? (
            <span
              className={`w-14 h-14 rounded-full ring-2 ring-amber-900/40 ${item.shimmer ? 'shadow-lg' : ''}`}
              style={{ background: item.color }}
            />
          ) : (
            <AvatarFigure
              config={{ ...baseConfig, [category]: item.id }}
              size={78}
              floating={false}
            />
          )}
        </div>
      </div>

      <div
        className="text-amber-50 font-bold text-xs leading-tight truncate"
        style={{ fontFamily: '"Fraunces", Georgia, serif' }}
      >
        {item.name}
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-1">
        {item.cost === 0 ? (
          <span className="text-[0.65rem] uppercase font-black tracking-widest text-emerald-300" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
            {item.owned ? 'Owned' : 'Free'}
          </span>
        ) : item.owned ? (
          <span className="text-[0.65rem] uppercase font-black tracking-widest text-emerald-300" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
            Owned
          </span>
        ) : (
          <span
            className={`text-[0.7rem] font-black inline-flex items-center gap-1 ${item.locked ? 'text-amber-500/60' : item.can_afford ? 'text-amber-200' : 'text-amber-400/70'}`}
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {item.cost}
          </span>
        )}
      </div>
      {item.locked && item.unlock && (
        <div
          className="text-[0.55rem] text-amber-400/80 mt-1 leading-tight"
          style={{ fontFamily: '"Barlow Condensed", system-ui' }}
        >
          🔒 {item.unlock.label}
          {typeof item.progress_now === 'number' && (
            <> ({item.progress_now}/{item.unlock.value})</>
          )}
        </div>
      )}
    </button>
  );
};

// Purchase confirmation modal — plain, direct, no dark patterns.
const PurchaseModal = ({ item, category, balance, baseConfig, onConfirm, onCancel, busy }) => (
  <div
    className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur"
    onClick={onCancel}
    data-testid="shop-purchase-modal"
  >
    <div
      className="max-w-md w-full rounded-2xl p-6 border-2 border-[#CBA135] bg-[#F4EEDF] shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-emerald-700" />
        <p className="text-xs uppercase tracking-widest font-black text-amber-900/80" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
          Confirm purchase
        </p>
      </div>
      <div className="flex items-center gap-4 mb-4">
        <div className="rounded-xl overflow-hidden bg-[#DCD3BD] p-2">
          <AvatarFigure config={{ ...baseConfig, [category]: item.id }} size={110} floating={false} />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-black text-[#241E17] leading-tight" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            {item.name}
          </div>
          <div className="text-[0.7rem] uppercase tracking-widest text-amber-900/70 mt-0.5" style={{ fontFamily: '"Barlow Condensed", system-ui' }}>
            {item.rarity}
          </div>
          <div className="mt-2 text-sm text-amber-950">
            <span className="font-black" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{item.cost}</span> Strides
            <span className="text-amber-900/70"> · you have <span className="font-black" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{balance}</span></span>
          </div>
        </div>
      </div>
      <p className="text-sm text-amber-950/80 mb-5">
        Owned forever after purchase. You&apos;ll be able to switch back to any earlier look for free.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="shop-purchase-cancel"
          className="flex-1 py-3 rounded-lg border-2 border-amber-900/40 bg-transparent text-amber-950 font-black hover:bg-amber-900/10 transition-all"
          style={{ fontFamily: '"Barlow Condensed", system-ui', letterSpacing: '0.1em' }}
        >
          NEVER MIND
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          data-testid="shop-purchase-confirm"
          className="flex-1 py-3 rounded-lg bg-[#C1502E] hover:bg-[#a13d21] text-amber-50 font-black transition-all shadow-lg disabled:opacity-60"
          style={{ fontFamily: '"Barlow Condensed", system-ui', letterSpacing: '0.1em' }}
        >
          {busy ? 'PURCHASING…' : `BUY FOR ${item.cost}`}
        </button>
      </div>
    </div>
  </div>
);

const TravelerPermitPage = () => {
  const { token, user } = useAuth();
  const [catalog, setCatalog] = useState({});
  const [avatarConfig, setAvatarConfig] = useState(DEFAULT_CONFIG_IDS);
  const [balance, setBalance] = useState(0);
  const [progress, setProgress] = useState({});
  const [activeCat, setActiveCat] = useState('skin');
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // V2 polish state
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('default');   // default | owned | price_asc | rarity
  const [celebrateItem, setCelebrateItem] = useState(null); // recent purchase — drives stamp-slam animation

  const loadCatalog = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/avatar/catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCatalog(res.data.catalog || {});
      setAvatarConfig(res.data.avatar_config || DEFAULT_CONFIG_IDS);
      setBalance(res.data.balance || 0);
      setProgress(res.data.progress || {});
    } catch (err) {
      toast.error('Could not load the Strides shop.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const activeCategoryCfg = catalog[activeCat] || null;
  const permitNumber = useMemo(() => {
    // Deterministic 5-digit "permit number" from the user's id/email so
    // it stays the same visit-to-visit but isn't private info.
    const seed = (user?.email || 'traveler').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return String(1000 + (seed % 89999)).padStart(5, '0');
  }, [user]);

  const personalizedCount = useMemo(() => {
    return Object.keys(DEFAULT_CONFIG_IDS).filter(
      (cat) => avatarConfig[cat] && avatarConfig[cat] !== DEFAULT_CONFIG_IDS[cat]
    ).length;
  }, [avatarConfig]);

  const handleItemClick = async (item) => {
    if (item.locked) {
      toast.info(item.unlock?.label || 'Locked — keep walking.', {
        description: typeof item.progress_now === 'number'
          ? `Progress so far: ${item.progress_now}/${item.unlock.value}`
          : undefined,
      });
      return;
    }
    if (item.owned) {
      // Owned → just wear it. Optimistic update, revert on error.
      const prev = avatarConfig[activeCat];
      setAvatarConfig((c) => ({ ...c, [activeCat]: item.id }));
      try {
        await axios.post(`${API}/avatar/wear`, {
          category: activeCat, item_id: item.id,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        setAvatarConfig((c) => ({ ...c, [activeCat]: prev }));
        toast.error('Could not save that look. Try again.');
      }
      return;
    }
    // Purchasable → open confirmation modal.
    if (!item.can_afford) {
      toast.error(`Not enough Strides — need ${item.cost}, you have ${balance}.`);
      return;
    }
    setPendingPurchase({ ...item, __category: activeCat });
  };

  const handleConfirmPurchase = async () => {
    if (!pendingPurchase) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/avatar/purchase`, {
        category: pendingPurchase.__category, item_id: pendingPurchase.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBalance(res.data.balance);
      // Optimistically wear the newly-owned item immediately.
      setAvatarConfig((c) => ({ ...c, [pendingPurchase.__category]: pendingPurchase.id }));
      await axios.post(`${API}/avatar/wear`, {
        category: pendingPurchase.__category, item_id: pendingPurchase.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Refresh catalog to update owned flags across the grid.
      await loadCatalog();
      toast.success(`${pendingPurchase.name} is yours.`);
      // Fire the celebration overlay — stamp slams in, holds ~1.4s, fades.
      setCelebrateItem({ name: pendingPurchase.name, rarity: pendingPurchase.rarity });
      setTimeout(() => setCelebrateItem(null), 1800);
      setPendingPurchase(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Purchase failed.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const totalCategories = Object.keys(DEFAULT_CONFIG_IDS).length;

  // V2 — filter + sort the active category's items. Search matches item name;
  // sort re-orders while keeping owned items pinned to the top on 'owned'.
  const displayedItems = useMemo(() => {
    if (!activeCategoryCfg) return [];
    const q = search.trim().toLowerCase();
    const rarityWeight = { legendary: 0, rare: 1, common: 2 };
    let list = activeCategoryCfg.items.filter((it) => !q || it.name.toLowerCase().includes(q));
    switch (sortBy) {
      case 'owned':
        list = [...list].sort((a, b) => (b.owned ? 1 : 0) - (a.owned ? 1 : 0));
        break;
      case 'price_asc':
        list = [...list].sort((a, b) => (a.cost || 0) - (b.cost || 0));
        break;
      case 'rarity':
        list = [...list].sort((a, b) => (rarityWeight[a.rarity] ?? 3) - (rarityWeight[b.rarity] ?? 3));
        break;
      default: break;
    }
    return list;
  }, [activeCategoryCfg, search, sortBy]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A1A14] text-amber-100 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-black mb-3" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>Sign in to visit the Strides shop</h1>
          <p className="text-amber-100/70 mb-4">Your traveler permit is tied to your account.</p>
          <Link to="/" className="inline-block px-4 py-2 rounded bg-[#C1502E] text-amber-50 font-black">Back home</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'radial-gradient(circle at 50% 20%, #16302a 0%, #0A1A14 60%)' }} data-testid="permit-loading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="grid lg:grid-cols-[380px,1fr] gap-8">
            {/* Permit skeleton */}
            <div className="rounded-2xl p-1" style={{ background: 'linear-gradient(140deg, #7d5f1c 0%, #CBA135 50%, #7d5f1c 100%)' }}>
              <div className="rounded-2xl bg-[#F4EEDF]/40 h-[540px] animate-pulse" />
            </div>
            {/* Grid skeleton */}
            <div className="space-y-4">
              <div className="h-6 w-32 bg-amber-900/30 rounded animate-pulse" />
              <div className="h-10 w-full bg-amber-900/20 rounded animate-pulse" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-40 rounded-xl bg-amber-900/20 animate-pulse" style={{ animationDelay: `${i * 60}ms` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative"
      style={{ background: 'radial-gradient(circle at 50% 20%, #16302a 0%, #0A1A14 60%)' }}
      data-testid="traveler-permit-page"
    >
      {/* Google Fonts — loaded once per page mount */}
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,800&family=Barlow+Condensed:wght@600;800&family=Source+Sans+3:wght@400;600&family=JetBrains+Mono:wght@600;800&display=swap" rel="stylesheet" />
      <style>{`
        .audiostride-figure-float { animation: audiostride-float 4s ease-in-out infinite; transform-origin: 100px 130px; }
        @keyframes audiostride-float {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-3px); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes stamp-slam {
          0%   { transform: rotate(-8deg) scale(3); opacity: 0; }
          25%  { transform: rotate(-8deg) scale(0.92); opacity: 1; }
          40%  { transform: rotate(-8deg) scale(1.08); }
          60%  { transform: rotate(-8deg) scale(1); }
          100% { transform: rotate(-8deg) scale(1); opacity: 0; }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
        @media (prefers-reduced-motion: reduce) {
          .audiostride-figure-float, [class*="shimmer"], [class*="stamp-slam"] { animation: none; }
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-amber-200 hover:text-amber-100 font-black tracking-widest text-sm"
            data-testid="permit-back-link"
            style={{ fontFamily: '"Barlow Condensed", system-ui' }}
          >
            <ArrowLeft className="w-4 h-4" />
            BACK
          </Link>
          <div
            className="text-xs uppercase tracking-widest text-amber-400/80"
            style={{ fontFamily: '"Barlow Condensed", system-ui' }}
          >
            AUDIOSTRIDE / TRAVELER PERMIT
          </div>
        </div>

        <div className="grid lg:grid-cols-[380px,1fr] gap-8 items-start">
          {/* Left: parchment permit card */}
          <div className="lg:sticky lg:top-6">
            <TravelerPermit
              userDisplay={user?.name || user?.email?.split('@')[0] || 'Traveler'}
              avatarConfig={avatarConfig}
              balance={balance}
              personalized={personalizedCount}
              total={totalCategories}
              permitNumber={permitNumber}
            />
          </div>

          {/* Right: rail + item grid — responsive:
              • Mobile / tablet (<lg): rail becomes a horizontal chip scroller above the grid
              • Desktop (≥lg): rail returns to a slim vertical column beside the grid */}
          <div className="lg:grid lg:grid-cols-[112px,1fr] lg:gap-4">
            {/* Rail */}
            <div className="mb-4 lg:mb-0 lg:border-r lg:border-amber-900/40 lg:pr-2" data-testid="shop-rail">
              <div className="flex flex-row overflow-x-auto lg:flex-col lg:overflow-visible gap-1 pb-1 lg:pb-0 no-scrollbar">
                {RAIL_GROUPS.map((g) => (
                  <React.Fragment key={g.label}>
                    <div
                      className="hidden lg:block text-[0.6rem] uppercase tracking-widest text-amber-500/70 font-black px-2 pt-3 pb-1"
                      style={{ fontFamily: '"Barlow Condensed", system-ui' }}
                    >
                      {g.label}
                    </div>
                    {g.cats.map((catId) => {
                      const cfg = catalog[catId];
                      if (!cfg) return null;
                      const active = activeCat === catId;
                      return (
                        <button
                          key={catId}
                          type="button"
                          onClick={() => { setActiveCat(catId); setSearch(''); }}
                          data-testid={`shop-rail-tab-${catId}`}
                          className={`flex-shrink-0 lg:w-full text-left py-2 px-3 rounded-lg text-[0.68rem] font-black uppercase tracking-widest transition-all whitespace-nowrap
                            ${active
                              ? 'bg-[#CBA135]/20 text-amber-200 border border-[#CBA135]/60 shadow-md shadow-[#CBA135]/10'
                              : 'text-amber-100/60 hover:text-amber-200 hover:bg-amber-900/20 border border-transparent'}`}
                          style={{ fontFamily: '"Barlow Condensed", system-ui' }}
                        >
                          {cfg.label}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Item grid */}
            <div className="min-w-0">
              {activeCategoryCfg ? (
                <>
                  <div className="mb-4">
                    <div
                      className="text-xs uppercase tracking-widest text-amber-500/80 font-black"
                      style={{ fontFamily: '"Barlow Condensed", system-ui' }}
                      data-testid="shop-cat-eyebrow"
                    >
                      {activeCategoryCfg.eyebrow}
                    </div>
                    <h2
                      className="text-3xl font-black text-amber-50 leading-tight"
                      style={{ fontFamily: '"Fraunces", Georgia, serif' }}
                      data-testid="shop-cat-title"
                    >
                      {activeCategoryCfg.label}
                    </h2>
                    <p
                      className="text-sm text-amber-100/70 mt-1 max-w-lg"
                      style={{ fontFamily: '"Source Sans 3", system-ui' }}
                    >
                      {activeCategoryCfg.description}
                    </p>
                  </div>

                  {/* Search + sort */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="relative flex-1 min-w-[180px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500/60" />
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search items"
                        data-testid="shop-search"
                        className="w-full pl-9 pr-8 py-2 rounded-lg bg-[#241E17] border border-amber-900/50 text-amber-100 placeholder-amber-100/40 focus:outline-none focus:border-[#CBA135] font-mono text-sm"
                      />
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-amber-500/60 hover:text-amber-300"
                          aria-label="Clear search"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {[
                        { id: 'default', label: 'Default' },
                        { id: 'owned', label: 'Owned first' },
                        { id: 'price_asc', label: 'Price ↑' },
                        { id: 'rarity', label: 'Rarity' },
                      ].map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setSortBy(s.id)}
                          data-testid={`shop-sort-${s.id}`}
                          className={`text-[0.65rem] uppercase tracking-widest font-black px-2.5 py-1.5 rounded-md transition-colors
                            ${sortBy === s.id ? 'bg-amber-900/60 text-amber-100 border border-amber-700/60' : 'text-amber-100/55 hover:text-amber-100 border border-transparent'}`}
                          style={{ fontFamily: '"Barlow Condensed", system-ui' }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {displayedItems.length === 0 ? (
                    <div className="text-center py-12 text-amber-100/60" data-testid="shop-empty-state">
                      <p className="text-sm" style={{ fontFamily: '"Source Sans 3", system-ui' }}>
                        No items match &quot;<span className="text-amber-200 font-bold">{search}</span>&quot; in {activeCategoryCfg.label}.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {displayedItems.map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          category={activeCat}
                          isSelected={avatarConfig[activeCat] === item.id}
                          baseConfig={avatarConfig}
                          onClick={handleItemClick}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-amber-200/70">No items available.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {pendingPurchase && (
        <PurchaseModal
          item={pendingPurchase}
          category={pendingPurchase.__category}
          baseConfig={avatarConfig}
          balance={balance}
          onConfirm={handleConfirmPurchase}
          onCancel={() => setPendingPurchase(null)}
          busy={busy}
        />
      )}

      {/* Purchase celebration — a stamp slams over the permit for ~1.8s */}
      {celebrateItem && (
        <div className="fixed inset-0 z-[90] pointer-events-none flex items-center justify-center" data-testid="purchase-celebration">
          <div
            className={`border-[6px] px-6 py-4 rounded-lg text-center shadow-2xl
              ${celebrateItem.rarity === 'legendary'
                ? 'border-[#CBA135] bg-[#F0DA92]/95 text-amber-950'
                : celebrateItem.rarity === 'rare'
                ? 'border-[#3E7484] bg-[#DBEAEC]/95 text-teal-900'
                : 'border-[#C1502E] bg-[#F4EEDF]/95 text-amber-950'}`}
            style={{ animation: 'stamp-slam 1.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards', fontFamily: '"Barlow Condensed", system-ui' }}
          >
            <div className="text-xs uppercase tracking-[0.3em] font-black opacity-70">Purchased</div>
            <div className="text-3xl font-black leading-tight mt-1" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>{celebrateItem.name}</div>
            <div className="text-[0.65rem] uppercase tracking-widest mt-1 font-black opacity-70">Yours forever</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelerPermitPage;
